import { Fragment } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table'
import { formatMoneyFull } from '@/lib/money'
import type { CellStatus } from '../utils/useSaveStatus'
import { Button } from '@/components/ui/Button'
import ProductLink from '@/components/productQuickView/ProductLink'
import PriceCellInput from './PriceCellInput'
import { RowSign, addedRowStyle, removedRowStyle } from '../../TopDown/components/DiffBits'
import { WasLine, cellChanged, rowChanged, type PricingDiff } from './pricingDiff'

export interface PriceLevel {
  id: string
  name: string
  color: string | null
}

export interface PriceCell {
  price: number
  currency: string
}

export interface Row {
  product_external_id: string
  product_name: string | null
  product_line_short_name: string | null
  prices_by_level_id: Record<string, PriceCell | null>
  asp: PriceCell | null
}

export interface Group {
  node: { id: string; name: string | null; ancestor_names: string[] }
  rows: Row[]
}

function formatMoney(amount: number, currency: string): string {
  return `${formatMoneyFull(amount, currency)} ${currency}`
}

// was: the published value when the review diff marks this cell as changed
// (undefined = no mark; null = the cell was empty before).
function MoneyCell({ cell, was, currency }: { cell: PriceCell | null; was?: number | null; currency?: string }) {
  return (
    <TableCell className="py-3 text-right text-[13px] text-foreground tabular-nums">
      {cell ? formatMoney(cell.price, cell.currency) : <span className="text-muted-foreground/40">—</span>}
      {was !== undefined && <WasLine was={was} currency={cell?.currency ?? currency ?? ''} />}
    </TableCell>
  )
}

interface Props {
  groups: Group[]
  priceLevels: PriceLevel[]
  canEdit: boolean
  emptyMessage: string
  // Only read when canEdit; the show page renders plain money cells.
  statusFor?: (productExternalId: string, level: string) => CellStatus | undefined
  onCellChange?: (productExternalId: string, level: string, price: number | null) => void
  onRemove?: (productExternalId: string) => void
  onAddProduct?: () => void
  // Review page: what the draft changes against the published rows. Adds
  // GitHub-style marks (green added rows, red removed rows, "was …" under a
  // changed cell) and, with changesOnly, hides untouched rows.
  diff?: PricingDiff | null
  changesOnly?: boolean
  // Currency for "was" amounts of cells that are empty now.
  currency?: string
}

// Prices and ASP for one sale channel, products grouped under their plan leaf
// node: Target ASP first, then one column per price level. Cells edit inline when the
// viewer can edit pricings.
export default function PricingTable({ groups, priceLevels, canEdit, emptyMessage, statusFor, onCellChange, onRemove, onAddProduct, diff, changesOnly = false, currency }: Props) {
  const headClass = 'h-10 px-4 text-[11px] font-semibold uppercase tracking-[0.08em]'
  const columnCount = 2 + priceLevels.length
  const added = new Set(diff?.added_product_ids ?? [])
  const shownGroups = diff && changesOnly
    ? groups.map((g) => ({ ...g, rows: g.rows.filter((r) => rowChanged(r, diff)) })).filter((g) => g.rows.length > 0)
    : groups

  // The published value to show under a cell, when the diff marks it changed.
  function wasFor(row: Row, level: string, value: PriceCell | null): number | null | undefined {
    if (!diff || added.has(row.product_external_id)) return undefined
    const was = level === 'asp' ? diff.asps_was[row.product_external_id] : diff.prices_was[row.product_external_id]?.[level]
    return cellChanged(value?.price, was) ? (was ?? null) : undefined
  }

  function cell(row: Row, level: string, value: PriceCell | null) {
    if (!canEdit) return <MoneyCell key={level} cell={value} was={wasFor(row, level, value)} currency={currency} />
    return (
      <TableCell key={level} className="py-1.5 px-2">
        <PriceCellInput
          initial={value?.price ?? null}
          currency={value?.currency ?? currency}
          status={statusFor?.(row.product_external_id, level)}
          onCommit={(price) => onCellChange?.(row.product_external_id, level, price)}
        />
      </TableCell>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className={headClass}>
            <span className="inline-flex items-center gap-3">
              Product
              {onAddProduct && (
                <Button size="sm" className="h-6 px-2 text-[11px] font-medium normal-case tracking-normal" onClick={onAddProduct}>
                  + Add Product
                </Button>
              )}
            </span>
          </TableHead>
          <TableHead className={`${headClass} text-right`}>Target ASP</TableHead>
          {priceLevels.map((pl) => (
            <TableHead key={pl.id} className={`${headClass} text-right`}>
              <span className="inline-flex flex-col items-end">
                <span className="inline-flex items-center gap-1.5">
                  {diff?.levels.added_ids.includes(pl.id) && <RowSign kind="added" />}
                  {pl.color && (
                    <span
                      aria-hidden
                      className="inline-block size-2 rounded-full"
                      style={{ background: pl.color }}
                    />
                  )}
                  {pl.name}
                </span>
                {diff?.levels.renamed[pl.id] && (
                  <span className="text-[10px] font-normal normal-case tracking-normal line-through" style={{ color: 'var(--destructive)' }}>
                    was {diff.levels.renamed[pl.id]}
                  </span>
                )}
              </span>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {shownGroups.length === 0 && (diff?.removed_products.length ?? 0) === 0 && (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columnCount} className="px-4 py-6 text-center text-[13px] text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        )}
        {shownGroups.map((group) => (
          <Fragment key={group.node.id}>
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columnCount} className="bg-muted/40 py-1.5 text-[11px]">
                {group.node.ancestor_names.length > 0 && (
                  <span className="text-muted-foreground">{group.node.ancestor_names.join(' › ')} › </span>
                )}
                <span className="font-semibold text-foreground">{group.node.name ?? '—'}</span>
              </TableCell>
            </TableRow>
            {group.rows.map((row) => (
              <TableRow
                key={row.product_external_id}
                className="group hover:bg-transparent"
                style={added.has(row.product_external_id) ? addedRowStyle : undefined}
              >
                <TableCell className="py-3 pl-1.5">
                  <div className="flex items-center gap-1.5">
                    {added.has(row.product_external_id) && <RowSign kind="added" />}
                    {onRemove && (
                      <button
                        type="button"
                        onClick={() => onRemove(row.product_external_id)}
                        aria-label={`Remove ${row.product_name ?? row.product_external_id} from this channel`}
                        title="Remove product from this channel (stored prices are kept)"
                        className="shrink-0 text-muted-foreground/50 hover:text-destructive p-1"
                      >
                        <svg width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden>
                          <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                    <div>
                      <div className="text-[13px] tracking-tight text-foreground">
                        {row.product_name ? (
                          <ProductLink externalId={row.product_external_id} className="text-[13px] tracking-tight">
                            {row.product_name}
                          </ProductLink>
                        ) : (
                          <span className="text-muted-foreground/60">—</span>
                        )}
                      </div>
                      {row.product_line_short_name && (
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          {row.product_line_short_name}
                        </div>
                      )}
                      <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                        {row.product_external_id}
                      </div>
                      {diff && row.product_external_id in diff.moved && (
                        <div className="mt-0.5 text-[10px]" style={{ color: 'var(--destructive)' }}>
                          was under {diff.moved[row.product_external_id] ?? '—'}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                {cell(row, 'asp', row.asp)}
                {priceLevels.map((pl) => cell(row, pl.id, row.prices_by_level_id[pl.id] ?? null))}
              </TableRow>
            ))}
          </Fragment>
        ))}
        {diff && diff.removed_products.length > 0 && (
          <Fragment>
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columnCount} className="bg-muted/40 py-1.5 text-[11px]">
                <span className="font-semibold" style={{ color: 'var(--destructive)' }}>Removed from this channel</span>
              </TableCell>
            </TableRow>
            {diff.removed_products.map((row) => (
              <TableRow key={`removed-${row.product_external_id}`} className="hover:bg-transparent" style={removedRowStyle}>
                <TableCell className="py-3 pl-1.5">
                  <div className="flex items-center gap-1.5">
                    <RowSign kind="removed" />
                    <div>
                      <div className="text-[13px] tracking-tight text-foreground">
                        {row.product_name ? (
                          <ProductLink externalId={row.product_external_id} className="text-[13px] tracking-tight">
                            {row.product_name}
                          </ProductLink>
                        ) : (
                          '—'
                        )}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                        {row.product_external_id}{row.node_name ? ` · was under ${row.node_name}` : ''}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <MoneyCell cell={row.asp} />
                {priceLevels.map((pl) => <MoneyCell key={pl.id} cell={row.prices_by_level_id[pl.id] ?? null} />)}
              </TableRow>
            ))}
          </Fragment>
        )}
      </TableBody>
    </Table>
  )
}
